import { useState, useEffect, useCallback, useMemo } from 'react';
import { api, UserFavoriteWithArche } from '@/lib/api';

/**
 * Full favorites management hook used by the Favorites page.
 * Provides the ordered list, toggle, and reorder functionality.
 */
export function useFavorites() {
  const [favorites, setFavorites] = useState<UserFavoriteWithArche[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const favoriteIds = useMemo(() => new Set(favorites.map((f) => f.archId)), [favorites]);

  const refresh = useCallback(async () => {
    try {
      const result = await api.getFavorites();
      setFavorites(result.favorites);
    } catch {
      // Silently fail — user may not be authenticated
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const isFavorite = useCallback((archId: string) => favoriteIds.has(archId), [favoriteIds]);

  const toggleFavorite = useCallback(
    async (archId: string) => {
      if (favoriteIds.has(archId)) {
        // Optimistic remove
        setFavorites((prev) => prev.filter((f) => f.archId !== archId));
        try {
          await api.removeFavorite(archId);
        } catch {
          await refresh();
        }
      } else {
        try {
          await api.addFavorite(archId);
          await refresh();
        } catch {
          // Ignore
        }
      }
    },
    [favoriteIds, refresh],
  );

  const reorder = useCallback(
    async (archIds: string[]) => {
      // Optimistic reorder
      const reordered = archIds
        .map((id) => favorites.find((f) => f.archId === id))
        .filter((f): f is UserFavoriteWithArche => f !== undefined);
      setFavorites(reordered);

      try {
        await api.reorderFavorites(archIds);
      } catch {
        await refresh();
      }
    },
    [favorites, refresh],
  );

  return { favorites, isLoading, isFavorite, toggleFavorite, reorder, refresh };
}
